import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { verificationAPI, paymentAPI, publicAPI } from '@/services/api';
import { Shield, Upload, CheckCircle, Clock, XCircle, CreditCard, FileText, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import WaiverCheckBox from "@/components/WaiverCheckBox";
import { defaultPricing } from "@/utils/costUtil";

// The 4 document slots the verification requires. Company registration is only
// required when the seller filled in a Business Name (i.e. registering as a
// company rather than an individual).
const DOC_SLOTS = [
	{ key: 'id_or_passport', required: true, alwaysShow: true },
	{ key: 'tax_number', required: true, alwaysShow: true },
	{ key: 'proof_of_address', required: true, alwaysShow: true },
	{ key: 'proof_of_registration', required: false, alwaysShow: false }, // shown when business_name is set
];

export default function SellerVerificationPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
	const [editId, setEditId] = useState(null);
	const [uploadingKey, setUploadingKey] = useState(null);
  const [form, setForm] = useState({
    business_name: '',
    vat_number: '',
    description: '',
    credentials_description: '',
		status: '',
		paid: false,
  });
  const [documents, setDocuments] = useState([]);
  const [verificationPrice, setVerificationPrice] = useState(25.00);
	const [pricing, setPricing] = useState(defaultPricing());
	const [vatPercent, setVatPercent] = useState(25.00);
	const [waivedWithdrawalRight, setWaivedWithdrawalRight] = useState(false);

  useEffect(() => {
    loadStatus();
    publicAPI.settings().then(r => {
			setPricing(r.data);
			if (r.data?.price_seller_verification) {
				setVerificationPrice(r.data.price_seller_verification);
			}
			if (r.data?.vat_percent) {
				setVatPercent(r.data.vat_percent);
			}
		}).catch(() => {});
  }, [user]);

  const loadStatus = async () => {
    try {
      const res = await verificationAPI.status();
      setStatus(res.data);
			const existingDocuments = (res.data.request.documents || []).map(doc => ({
				...doc,
				isExisting: true
			}));
			setDocuments(existingDocuments);
			setForm({
				business_name: res.data.request.business_name || form.business_name || '',
				vat_number: res.data.request.vat_number || form.vat_number || '',
				description: res.data.request.description || form.description || '',
				credentials_description: res.data.request.credentials_description || form.credentials_description || '',
				paid: res.data.request.paid || form.paid || false,
			});
			setEditId(res.data.request.id);
    } catch {}
    setLoading(false);
  };

	const isVerified = status?.is_verified || user?.is_verified;
	const request = status?.request;
	const uploadedByType = {};
	documents.forEach(d => {
		if (d && typeof d === 'object' && d.doc_type) uploadedByType[d.doc_type] = d;
	});

	const isCompany = !!form.business_name?.trim() || !!request?.business_name?.trim();
	const visibleSlots = DOC_SLOTS.filter(s => s.alwaysShow || (s.key === 'proof_of_registration' && isCompany));

	const missingRequired = visibleSlots
		.filter(s => s.required || (s.key === 'proof_of_registration' && isCompany))
		.filter(s => !uploadedByType[s.key]);

	const uploadDoc = async (file, docType) => {
		if (!file) return;
		// Ensure a request exists so the doc is attached
		if (!request) {
			try {
				form.status = 'uploading';
				await verificationAPI.request(form);
			} catch (e) {
				toast.error(e?.response?.data?.detail || 'Failed to start request');
				return;
			}
		}
		setUploadingKey(docType);
		try {
			const fd = new FormData();
			fd.append('file', file);
			fd.append('doc_type', docType);
			await verificationAPI.uploadDocument(fd);
			toast.success(t('verification.doc_uploaded'));
			await loadStatus();
		} catch (e) {
			toast.error(e?.response?.data?.detail || t('common.error'));
		} finally {
			setUploadingKey(null);
		}
	};

	const removeDoc = async (docId) => {
		try {
			await verificationAPI.deleteDocument(docId);
			toast.success(t('verification.doc_removed'));
			await loadStatus();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.credentials_description) {
      toast.error(t('verification.err_fill_form'));
      return;
    }
		if (missingRequired.length > 0) {
			toast.error(t('verification.err_missing_docs'));
			return;
		}
    setSubmitting(true);
    try {
			const newDocs = documents.filter(i => !i.isExisting);
			form.status = 'awaiting_payment';
			if (editId) {
				await verificationAPI.update(editId, form);
			} else {
				await verificationAPI.request(form);
			}
			// Upload new docs only
			for (const doc of newDocs) {
				const formData = new FormData();
				formData.append('file', doc);
				await verificationAPI.uploadDocument(formData);
			}

			if (editId) {
				toast.success(t('verification.request_submitted'));
			} else {
				toast.success(t('verification.request_submitted_proceed_payment'));
			}
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('verification.request_failed'));
    }
    setSubmitting(false);
  };

  const proceedToPayment = async () => {
    try {
      const res = await paymentAPI.checkout({
				checkout_type: 'seller_verification',
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(t('verification.payment_error'));
    }
  };

  if (loading) return (
    <div className="pt-24 min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#1A3C34] border-t-transparent rounded-full animate-spin" />
    </div>
  );

	const vatDivisor = 1 + (vatPercent / 100);
	const subTotal = (verificationPrice / vatDivisor).toFixed(2);
	const vatAmount = (verificationPrice - subTotal).toFixed(2);

	return (
    <div className="pt-20 md:pt-24 min-h-screen" data-testid="seller-verification-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-[#8B4513]" />
          <div>
            <h1 className="text-3xl font-medium text-[#1A3C34] font-['Playfair_Display']">
							{ t('verification.seller_verification_title') }
						</h1>
           	<p className="text-[#666055] text-sm mt-1">
							{ t('verification.not_verified_message') }
						</p>
					</div>
        </div>

        {/* Already Verified */}
        {isVerified && (
          <div className="bg-white border border-green-200 rounded-sm p-8 text-center animate-fade-in-up"
							 data-testid="verified-status">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-[#1A3C34] font-['Playfair_Display'] mb-2">
							{ t('verification.already_verified_title') }
						</h2>
            <p className="text-[#666055] mb-6">
							{ t('verification.already_verified_message') }
						</p>
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 px-4 py-2 rounded-sm">
              <Shield className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-green-700">
								{ t('verification.verified_seller') }
							</span>
            </div>
          </div>
        )}

        {/* Pending Review */}
        {!isVerified && request?.status === 'pending' && (
          <div className="bg-white border border-[#E6E2D6] rounded-sm p-8 text-center animate-fade-in-up"
							 data-testid="pending-status">
            <Clock className="w-16 h-16 text-[#8B4513] mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-[#1A3C34] font-['Playfair_Display'] mb-2">
							{ t('verification.verification_under_review_title') }
						</h2>
            <p className="text-[#666055] mb-4">
							{ t('verification.verification_under_review_message') }
						</p>
            <Badge className="bg-[#F2F0E9] text-[#8B4513] rounded-sm px-4 py-1">
							{ t('verification.pending_review') }
						</Badge>
          </div>
        )}

        {/* Awaiting Payment */}
        {!isVerified && request?.status === 'awaiting_payment' && (
          <div className="bg-white border border-[#E6E2D6] rounded-sm p-8 text-center animate-fade-in-up"
							 data-testid="awaiting-payment-status">
            <CreditCard className="w-16 h-16 text-[#8B4513] mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-[#1A3C34] font-['Playfair_Display'] mb-2">
							{ t('verification.complete_payment_title') }
						</h2>
            <p className="text-[#666055] mb-6">
							{ t('verification.complete_payment_message') }
						</p>
            <div className="bg-[#F9F7F2] border border-[#E6E2D6] rounded-sm p-4 mb-6 max-w-xs mx-auto">
              <p className="text-sm text-[#666055]">{ t('verification.verification_fee') }</p>
              <p className="text-3xl font-bold text-[#8B4513]" data-testid="verification-fee">
								{verificationPrice.toFixed(2)} EUR
							</p>
              <p className="text-xs text-[#666055] mt-1">
								{ t('verification.payment_message') }
							</p>
            </div>
            <Button onClick={proceedToPayment} className="bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm h-12 px-8 btn-equi"
										data-testid="pay-verification-btn">
              <CreditCard className="w-4 h-4 mr-2" /> { t('verification.payment_button_message') }
            </Button>
          </div>
        )}

        {/* Rejected */}
        {!isVerified && request?.status === 'rejected' && (
          <div className="bg-white border border-red-200 rounded-sm p-8 animate-fade-in-up"
							 data-testid="rejected-status">
            <div className="text-center mb-6">
              <XCircle className="w-16 h-16 text-[#9B2C2C] mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-[#1A3C34] font-['Playfair_Display'] mb-2">
								{ t('verification.verification_failed_title') }
							</h2>
            </div>
            {(request.admin_notes || request.notes) && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-6">
                <p className="text-sm text-[#9B2C2C] font-medium mb-1">
									{ t('verification.verification_failed_reason') }:
								</p>
                <p className="text-sm text-[#666055]">{request.admin_notes || request.notes}</p>
              </div>
            )}
            <p className="text-[#666055] text-center">
							{ t('verification.verification_resubmit_message') }
						</p>
          </div>
        )}

        {/* New Request Form */}
        {!isVerified && (!request || request.status === 'rejected' || request.status === 'none' || request.status === 'uploading') && (
          <form onSubmit={handleSubmit} className="space-y-6 mt-8 animate-fade-in-up"
								data-testid="verification-form">
            {/* Benefits */}
            <div className="bg-[#1A3C34] rounded-sm p-6 text-white">
              <h3 className="text-lg font-semibold font-['Playfair_Display'] mb-4">
								{ t('verification.verification_benefits_title') }
							</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
									t('verification.verification_benefits_message1'),
									t('verification.verification_benefits_message2'),
									t('verification.verification_benefits_message3'),
									t('verification.verification_benefits_message4')
                ].map((b, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#D4C5A9] shrink-0 mt-0.5" />
                    <span className="text-sm text-white/80">{b}</span>
                  </div>
                ))}
              </div>
							<div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-between">
                <span className="text-sm text-white/60">
									{ t('payment.subtotal') }
								</span>
								<span className="text-sm font-bold text-[#D4C5A9]" data-testid="verification-fee-banner">
									{subTotal} EUR
								</span>
							</div>
							<div className="mt-1 pt-4 border-white/20 flex items-center justify-between">
                <span className="text-sm text-white/60">
									{ t('admin.vat_percent') } ({vatPercent} %)
								</span>
								<span className="text-sm font-bold text-[#D4C5A9]" data-testid="verification-fee-banner">
									{vatAmount} EUR
								</span>
							</div>
              <div className="mt-4 pt-4 border-white/20 flex items-center justify-between">
                <span className="text-sm text-white/60">
									{ t('verification.one_time_verification_fee') }
								</span>
                <span className="text-2xl font-bold text-[#D4C5A9]" data-testid="verification-fee-banner">
									{verificationPrice.toFixed(2)} EUR
								</span>
              </div>
            </div>

						{/* Form fields */}
            <div className="bg-white border border-[#E6E2D6] rounded-sm p-6 space-y-4">
              <h3 className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display']">
								{ t('verification.user_details_title') }
							</h3>
              <div>
                <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{ t('verification.business_name_label') }
								</Label>
                <Input value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})}
											 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
											 placeholder={ t('verification.business_name_placeholder') }
											 data-testid="ver-business-name" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{ t('verification.vat_number_label') }
								</Label>
                <Input value={form.vat_number} onChange={e => setForm({...form, vat_number: e.target.value})}
											 className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
											 placeholder={ t('verification.vat_number_placeholder') } data-testid="ver-vat" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{ t('verification.describe_business_label') } *
								</Label>
                <Textarea required value={form.description}
													onChange={e => setForm({...form, description: e.target.value})}
													className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm min-h-[80px]"
													placeholder={ t('verification.describe_business_placeholder') }
													data-testid="ver-description" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#666055] uppercase tracking-wider">
									{ t('verification.credentials_label') } *
								</Label>
                <Textarea required value={form.credentials_description}
													onChange={e => setForm({...form, credentials_description: e.target.value})}
													className="mt-1 bg-[#F9F7F2] border-[#E6E2D6] rounded-sm min-h-[80px]"
													placeholder={ t('verification.credentials_placeholder') }
													data-testid="ver-credentials" />
              </div>
            </div>

						{/* Required documents checklist */}
						<div className="bg-white border border-[#E6E2D6] rounded-sm p-6" data-testid="ver-docs-checklist">
							<h3 className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display'] mb-1">
								{ t('verification.docs_title') }
							</h3>
							<p className="text-sm text-[#666055] mb-4">{t('verification.docs_subtitle')}</p>
							<div className="space-y-3">
								{visibleSlots.map(slot => {
									const doc = uploadedByType[slot.key];
									const uploaded = !!doc;
									const isRequired = slot.required || (slot.key === 'proof_of_registration' && isCompany);
									return (
										<div
											key={slot.key}
											className={`border rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${uploaded ? 
												'bg-green-50 border-green-200' : 'bg-[#F9F7F2] border-[#E6E2D6]'}`}
											data-testid={`ver-doc-row-${slot.key}`}
										>
											<div className="flex items-center gap-3 flex-1 min-w-0">
												{uploaded ? (
													<CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
												) : (
													<div className="w-5 h-5 rounded-full border-2 border-[#D4C5A9] shrink-0" />
												)}
												<div className="min-w-0">
													<div className="text-sm font-semibold text-[#1A3C34] flex items-center gap-2 flex-wrap">
														{ t(`verification.doc.${slot.key}.label`) }
														{ isRequired &&
															<span className="text-[10px] font-bold text-[#8B4513] uppercase tracking-wider">
																{t('verification.required')}
															</span>
														}
													</div>
													<p className="text-xs text-[#666055] mt-0.5">{t(`verification.doc.${slot.key}.hint`)}</p>
													{uploaded && (
														<p className="text-xs text-green-700 mt-1 flex items-center gap-1 truncate">
															<FileText className="w-3 h-3 shrink-0" /> <span className="truncate">{doc.filename}</span>
														</p>
													)}
												</div>
											</div>
											<div className="flex items-center gap-2 shrink-0">
												<label className="cursor-pointer">
													<input
														type="file"
														className="hidden"
														accept="image/*,application/pdf"
														disabled={uploadingKey === slot.key}
														onChange={e => {
															const f = e.target.files?.[0]; e.target.value = ''; uploadDoc(f, slot.key);
														}}
														data-testid={`ver-doc-input-${slot.key}`}
													/>
													<span className={`inline-flex items-center gap-1 px-3 py-2 rounded-sm text-xs font-medium 
															border transition-colors ${uploadingKey === slot.key ? 'bg-[#F2F0E9] border-[#E6E2D6] ' +
															'text-[#666055]' : uploaded ? 'bg-white border-[#E6E2D6] text-[#8B4513] hover:bg-[#F2F0E9]' : 
															'bg-[#8B4513] border-[#8B4513] text-white hover:bg-[#6F3610]'}`}>
                            <Upload className="w-3 h-3" />
														{uploadingKey === slot.key ?
															t('common.loading') : uploaded ?
																t('verification.replace') : t('verification.upload')}
                          </span>
												</label>
												{uploaded && (
													<button
														type="button"
														onClick={() => removeDoc(doc.id)}
														className="p-2 text-[#9B2C2C] hover:bg-red-50 rounded-sm"
														aria-label={t('verification.remove')}
														data-testid={`ver-doc-remove-${slot.key}`}
													>
														<Trash2 className="w-4 h-4" />
													</button>
												)}
											</div>
										</div>
									);
								})}
              </div>
							{missingRequired.length > 0 && (
								<p className="text-xs text-[#9B2C2C] mt-3" data-testid="ver-missing-docs-warn">
									{t('verification.missing_hint', { count: missingRequired.length })}
								</p>
							)}
            </div>

						<WaiverCheckBox
							images={ [] }
							videos={ [] }
							pricing={ pricing }
							includedImages={ 5 }
							includedVideos={ 3 }
							isFeatured={ true }
							featuredPaid={ false }
							onClick={ setWaivedWithdrawalRight }
							state={ waivedWithdrawalRight }
							t={ t }/>

						<Button
							type="submit"
							disabled={submitting || missingRequired.length > 0 || !waivedWithdrawalRight}
							className="w-full bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-14 text-base font-medium btn-equi
							 	disabled:opacity-60 disabled:cursor-not-allowed"
							data-testid="submit-verification-btn">
							{submitting ? t('common.loading') : t('verification.submit_btn')}
						</Button>
          </form>
        )}

				{/* Uploaded docs also visible when awaiting payment or pending */}
				{!isVerified && request && ['awaiting_payment', 'pending'].includes(request.status) && documents.length > 0 && (
					<div className="bg-white border border-[#E6E2D6] rounded-sm p-6 mt-6" data-testid="ver-docs-summary">
						<h3 className="text-sm font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
							{t('verification.your_docs')}
						</h3>
						<ul className="space-y-2">
							{documents.filter(d => d && typeof d === 'object').map(d => (
								<li key={d.id} className="flex items-center justify-between text-sm bg-[#F9F7F2] px-3 py-2 rounded-sm
										border border-[#E6E2D6]">
                  <span className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-[#8B4513] shrink-0" />
                    <span className="font-medium text-[#1A3C34] shrink-0">
											{t(`verification.doc.${d.doc_type}.label`, d.doc_type)}
										</span>
                    <span className="text-[#666055] truncate">· {d.filename}</span>
                  </span>
									<button
										type="button"
										onClick={() => verificationAPI.viewDocument(d.url, d.filename)
											.catch(() => toast.error(t('common.error')))}
										className="text-xs text-[#8B4513] hover:underline flex items-center gap-1 shrink-0"
										data-testid={`ver-doc-view-${d.id}`} >
										<ExternalLink className="w-3 h-3" /> {t('verification.view')}
									</button>
								</li>
							))}
						</ul>
					</div>
				)}
      </div>
    </div>
  );
}
